"use client";

import { create } from "zustand";
import {
  getVirtualTryOnGenerationResult,
  updateVirtualTryOnJob,
  type VirtualTryOnJob,
} from "../virtualTryOnJobState";

export type VirtualTryOnJobInput = Pick<
  VirtualTryOnJob,
  "productName" | "sku" | "locale" | "variantId"
> & {
  photo: File;
};

type GenerateErrorResponse = {
  error?: string;
};

function getGenerateError(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const error = (data as GenerateErrorResponse).error;
  return typeof error === "string" ? error : null;
}

interface VirtualTryOnJobsStore {
  jobs: VirtualTryOnJob[];
  selectedJobId: string | null;
  awaitingResultDialog: boolean;
  setSelectedJobId: (id: string | null) => void;
  setAwaitingResultDialog: (awaiting: boolean) => void;
  removeJob: (id: string) => void;
  submitJob: (input: VirtualTryOnJobInput) => void;
}

export const useVirtualTryOnJobsStore = create<VirtualTryOnJobsStore>((set, get) => ({
  jobs: [],
  selectedJobId: null,
  awaitingResultDialog: false,
  setSelectedJobId: (id) => set({ selectedJobId: id }),
  setAwaitingResultDialog: (awaiting) => set({ awaitingResultDialog: awaiting }),
  removeJob: (id) =>
    set((state) => ({
      jobs: state.jobs.filter((job) => job.id !== id),
      selectedJobId: state.selectedJobId === id ? null : state.selectedJobId,
    })),
  submitJob: ({ photo, ...input }) => {
    const id = crypto.randomUUID();
    set((state) => ({
      jobs: [{ ...input, id, status: "generating" as const }, ...state.jobs],
      awaitingResultDialog: true,
    }));

    const settleJob = (patch: Partial<Omit<VirtualTryOnJob, "id">>) => {
      const shouldOpen = get().awaitingResultDialog;
      set((state) => ({
        jobs: updateVirtualTryOnJob(state.jobs, id, patch),
        selectedJobId: shouldOpen ? id : state.selectedJobId,
        awaitingResultDialog: shouldOpen ? false : state.awaitingResultDialog,
      }));
    };

    void (async () => {
      const form = new FormData();
      form.set("sku", input.sku);
      form.set("locale", input.locale);
      form.set("variantId", input.variantId);
      form.set("photo", photo);

      try {
        const response = await fetch(`/${input.locale}/api/virtual-try-on`, {
          method: "POST",
          body: form,
        });
        const data: unknown = await response.json();
        if (!response.ok) {
          settleJob({
            status: "failed",
            errorKey: getGenerateError(data) ?? "errors.virtual_try_on_upstream",
          });
          return;
        }
        const result = getVirtualTryOnGenerationResult(data);
        if (!result) {
          settleJob({ status: "failed", errorKey: "errors.virtual_try_on_upstream" });
          return;
        }
        settleJob({
          status: "completed",
          ...result,
        });
      } catch {
        settleJob({ status: "failed", errorKey: "errors.virtual_try_on_upstream" });
      }
    })();
  },
}));
